/**
 * Error Tracking Utilities
 *
 * Helper functions for tracking and handling errors across the application
 * Integrates with Sentry for comprehensive error tracking and debugging
 */

import React from 'react';
import { captureSentryException } from '../sentry';
import { trackError } from '../analytics';

/**
 * Wrap an async function with error tracking
 * Automatically captures errors to Sentry and Analytics
 *
 * @param {Function} fn - Async function to wrap
 * @param {string} operation - Name of the operation for tracking
 * @param {Object} context - Additional context for error tracking
 * @returns {Function} - Wrapped function
 */
export function withErrorTracking(fn, operation, context = {}) {
  return async (...args) => {
    try {
      const result = await fn(...args);
      return result;
    } catch (error) {
      // Track error
      trackError(operation, error.message, {
        error,
        ...context
      });

      // Re-throw to allow caller to handle
      throw error;
    }
  };
}

/**
 * Track API call errors with additional context
 *
 * @param {Error} error - The error object
 * @param {string} endpoint - API endpoint that failed
 * @param {Object} options - Additional options
 */
export function trackApiError(error, endpoint, options = {}) {
  const context = {
    error,
    endpoint,
    method: options.method || 'GET',
    statusCode: error.response?.status,
    ...options
  };

  trackError('api_error', error.message, context);
  // Note: No breadcrumb needed - trackError() already reports the error to Sentry
}

/**
 * Track Firebase operation errors
 *
 * @param {Error} error - The error object
 * @param {string} operation - Firebase operation name
 * @param {Object} context - Additional context
 */
export function trackFirebaseError(error, operation, context = {}) {
  const errorContext = {
    error,
    operation,
    code: error.code,
    ...context
  };

  trackError('firebase_error', error.message, errorContext);
  // Note: No breadcrumb needed - trackError() already reports the error to Sentry
}

/**
 * Track authentication errors
 *
 * @param {Error} error - The error object
 * @param {string} method - Authentication method
 * @param {Object} context - Additional context
 */
export function trackAuthError(error, method, context = {}) {
  const errorContext = {
    error,
    method,
    code: error.code,
    ...context
  };

  trackError('auth_error', error.message, errorContext);
  // Note: No breadcrumb needed - trackError() already reports the error to Sentry
}

/**
 * Track blockchain/wallet errors
 *
 * @param {Error} error - The error object
 * @param {string} operation - Blockchain operation
 * @param {Object} context - Additional context
 */
export function trackBlockchainError(error, operation, context = {}) {
  const errorContext = {
    error,
    operation,
    code: error.code,
    ...context
  };

  trackError('blockchain_error', error.message, errorContext);
  // Note: No breadcrumb needed - trackError() already reports the error to Sentry
}

/**
 * Create an error boundary for functional components
 * Returns error state and handlers
 */
export function useErrorBoundary() {
  const [error, setError] = React.useState(null);

  const captureError = (error, context = {}) => {
    setError(error);
    captureSentryException(error, context);
  };

  const resetError = () => {
    setError(null);
  };

  return { error, captureError, resetError };
}

/**
 * Safe async wrapper that catches and logs errors
 * Returns [error, data] tuple
 *
 * @param {Promise} promise - Promise to execute
 * @param {string} operation - Operation name for tracking
 * @returns {Promise<[Error|null, any]>} - [error, data] tuple
 */
export async function safeAsync(promise, operation = 'unknown') {
  try {
    const data = await promise;
    return [null, data];
  } catch (error) {
    trackError(operation, error.message, { error });
    return [error, null];
  }
}

/**
 * Handle promise rejection globally
 * Call this to set up global unhandled rejection handler
 */
export function setupGlobalErrorHandlers() {
  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);

    trackError('unhandled_rejection', event.reason?.message || 'Unknown error', {
      error: event.reason,
      promise: event.promise
    });

    // Prevent default browser console warning
    // (we're already logging it)
    event.preventDefault();
  });

  // Global error handler
  window.addEventListener('error', (event) => {
    console.error('Global error:', event.error);

    // Skip errors from browser extensions
    if (event.filename && (
      event.filename.includes('chrome-extension://') ||
      event.filename.includes('moz-extension://')
    )) {
      return;
    }

    trackError('global_error', event.message, {
      error: event.error,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    });
  });
}

const errorTrackingUtils = {
  withErrorTracking,
  trackApiError,
  trackFirebaseError,
  trackAuthError,
  trackBlockchainError,
  safeAsync,
  setupGlobalErrorHandlers
};

export default errorTrackingUtils;
